/*
 * While the tab is hidden: `(n)` agents waiting on the title (added to the
 * unread notifications count by the shared title manager) and a dot on the
 * firm favicon, red if anything is blocked or broke, else green.
 */
import { useEffect, useState } from "react";

import { setTitleCount } from "@/lib/documentTitle";

import { groupTone, type SignalTone } from "../signalTone";
import type { AttentionItem } from "../types";

export const ATTENTION_TITLE_SOURCE = "agents";

const DOT: Record<SignalTone, string> = { red: "#dc2626", green: "#059669" };
const ORIGINAL = "attentionOriginalHref";

const iconLinks = (): HTMLLinkElement[] =>
  Array.from(document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']"));

const restoreFavicon = () => {
  for (const link of iconLinks()) {
    const original = link.dataset[ORIGINAL];
    if (original !== undefined) {
      link.href = original;
      delete link.dataset[ORIGINAL];
    }
  }
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/** The favicon with a coloured dot at the bottom right, as a data URL; null when it can't be drawn. */
export const drawFaviconDot = async (
  src: string,
  color: string
): Promise<string | null> => {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext?.("2d");
  if (!ctx) return null;
  try {
    ctx.drawImage(await loadImage(src), 0, 0, 64, 64);
  } catch {
    // Draw the dot alone rather than nothing.
  }
  ctx.beginPath();
  ctx.arc(46, 46, 16, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = 5;
  ctx.strokeStyle = "#ffffff";
  ctx.stroke();
  try {
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
};

const paintFavicon = async (tone: SignalTone) => {
  const links = iconLinks();
  if (!links.length) return;
  const source = links[0].dataset[ORIGINAL] ?? links[0].href;
  const url = await drawFaviconDot(source, DOT[tone]);
  if (!url) return;
  for (const link of iconLinks()) {
    if (link.dataset[ORIGINAL] === undefined)
      link.dataset[ORIGINAL] = link.href;
    link.href = url;
  }
};

export const useDocumentHidden = (): boolean => {
  const [hidden, setHidden] = useState(
    () => typeof document !== "undefined" && document.hidden
  );
  useEffect(() => {
    const on = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);
  return hidden;
};

export function useAttentionDocumentSignals(items: AttentionItem[]): void {
  const hidden = useDocumentHidden();
  const count = hidden ? items.length : 0;
  const tone = hidden ? groupTone(items) : null;

  useEffect(() => {
    setTitleCount(ATTENTION_TITLE_SOURCE, count);
  }, [count]);
  useEffect(() => () => setTitleCount(ATTENTION_TITLE_SOURCE, 0), []);

  useEffect(() => {
    if (!tone) {
      restoreFavicon();
      return undefined;
    }
    let cancelled = false;
    void paintFavicon(tone).then(() => {
      if (cancelled) restoreFavicon();
    });
    return () => {
      cancelled = true;
    };
  }, [tone]);
  useEffect(() => restoreFavicon, []);
}
